/*
 * business-card/photo.js — a photograph as the background of a card.
 *
 * WHY THE EFFECTS ARE BAKED INTO PIXELS
 * ─────────────────────────────────────
 * A PDF cannot blur, cannot desaturate and cannot tint. Every one of those is
 * a raster operation, and the only question is WHERE it happens. If the preview
 * blurred on the canvas and the exporter shipped the untouched file, the two
 * would differ in the one way this tool exists to prevent: the reader approves
 * something on screen that the printer never receives.
 *
 * So there is ONE function, `bake`, and both back ends call it. The preview
 * calls it at the size of the preview canvas; the exporter calls it at the size
 * the card is actually printed at. Same code, same order of operations, two
 * resolutions — which is why every distance here is in MILLIMETRES and
 * converted to pixels against the output size at the last moment. A blur radius
 * in pixels would mean one thing at 300 px wide and something four times weaker
 * at 1100, and the preview would flatter the file.
 *
 * WHY THERE IS A "VEIL" AND NOT A `brightness()` FILTER
 * ────────────────────────────────────────────────────
 * The reason anyone reaches for "осветлить" on a background is that the text on
 * top of it cannot be read. `brightness()` raises every channel, which lifts
 * the darks and blows the lights out — a sky goes white while the shadows the
 * text sits over barely move. A flat veil of the card's own paper colour at
 * N % opacity does exactly what is being asked for: it moves the WHOLE image a
 * measurable distance toward the paper, so the contrast ratio underneath the
 * text moves with it and preflight can grade the result. The same slider run
 * the other way lays ink instead of paper, for white type.
 *
 * ⚠️ THE BLUR IS DRAWN OVERSIZE AND CROPPED. `ctx.filter = 'blur(Npx)'` samples
 * transparent black outside the source rectangle, so a blurred image drawn at
 * its exact destination size gets a dark, soft frame N pixels wide on all four
 * edges — on a full-bleed background that lands as a grey halo around the cut
 * card. Drawing into a canvas padded by 2N and copying the middle out costs one
 * extra allocation and removes the artefact completely.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.CardPhoto = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    /* The neutral position of every knob. A spec equal to this one is a
     * photograph placed and left alone. */
    var DEFAULTS = {
        fit: 'cover',        // 'cover' fills the card, 'contain' shows all of it
        blur: 0,             // mm
        light: 0,            // −100 (toward ink) … +100 (toward paper)
        sat: 100,            // 0 = greyscale … 150
        tint: 0,             // 0 … 100 % toward the brand colour
        rot: 0,              // 0 | 90 | 180 | 270 — quarter turns, clockwise on the card
        flip: false,         // mirrored left-to-right, as SEEN (after the turn)
        side: 'front',
    };

    function num(v, d) {
        var n = typeof v === 'string' ? parseFloat(v) : v;
        return (typeof n === 'number' && isFinite(n)) ? n : d;
    }
    function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }
    /* Quarter turns only. A free angle would leave paper-coloured wedges at
     * the corners of a full-bleed background and turn "cover" into a
     * trigonometry problem; what people actually reach for is "this came in
     * sideways" and "the face looks the wrong way", and both are here. */
    function quarterTurn(v) {
        var n = Math.round(num(v, 0) / 90) * 90 % 360;
        return n < 0 ? n + 360 : n;
    }

    function normalise(spec) {
        spec = spec || {};
        return {
            src: spec.src || '',
            fit: spec.fit === 'contain' ? 'contain' : 'cover',
            blur: clamp(num(spec.blur, 0), 0, 4),
            light: clamp(num(spec.light, 0), -100, 100),
            sat: clamp(num(spec.sat, 100), 0, 150),
            tint: clamp(num(spec.tint, 0), 0, 100),
            rot: quarterTurn(spec.rot),
            flip: !!spec.flip,
        };
    }

    /* Is this background doing nothing at all? Used to keep an untouched
     * photograph off the "converted to CMYK" work list and out of preflight. */
    function isPlain(spec) {
        var s = normalise(spec);
        return s.blur === 0 && s.light === 0 && s.sat === 100 && s.tint === 0;
    }

    /* A cache key for one baked result. Everything that changes a pixel is in
     * it, INCLUDING the two resolved colours: the veil is made of the card's
     * paper and the tint of its accent, so switching palette changes the image
     * even though no slider moved. Forgetting either one is a stale preview
     * that looks like a rendering bug.
     *
     * The picture itself is fingerprinted (length + tail), never included whole:
     * a data URL is megabytes, this key is built on every redraw, and comparing
     * megabytes of base64 per keystroke to find out that nothing changed is the
     * cost the cache exists to avoid. Two different photographs with the same
     * byte length AND the same last 24 base64 characters would share a bake;
     * the same photograph on both sides deliberately does. */
    function key(spec, place, colours, out) {
        var s = normalise(spec);
        return [
            s.src.length, s.src.slice(-24), s.fit, s.blur, s.light, s.sat, s.tint,
            s.rot, s.flip ? 1 : 0,
            r1(place && place.zoom), r1(place && place.dx), r1(place && place.dy),
            r1(place && place.w), r1(place && place.h),
            (colours && colours.paper || []).join(','),
            (colours && colours.tint || []).join(','),
            out && out.w, out && out.h,
        ].join('|');
    }
    function r1(v) { return Math.round(num(v, 0) * 100) / 100; }

    /* ── where the photograph sits inside the card ──────────────────────────
     * `place` is in millimetres: the box to fill, plus the user's zoom and pan.
     * Pan is in millimetres of CARD, not fractions of the image, so dragging
     * the photo moves it by the distance the pointer moved — which is the only
     * behaviour that feels like dragging a photograph.
     */
    function sourceRect(img, place, spec) {
        var s = normalise(spec);
        var boxW = place.w, boxH = place.h;
        var natW = img.naturalWidth || img.width || 1;
        var natH = img.naturalHeight || img.height || 1;
        /* The card sees the TURNED picture: a quarter turn swaps the two sides,
         * and every decision below — fit, zoom, the dpi that preflight grades —
         * is made on the swapped pair. Making it on the file's own pair would
         * "cover" the card with a portrait photograph turned landscape and
         * leave paper showing along the long edges. */
        var quarter = (s.rot === 90 || s.rot === 270);
        var srcW = quarter ? natH : natW, srcH = quarter ? natW : natH;
        var zoom = clamp(num(place.zoom, 1), 0.25, 6);

        var scale = s.fit === 'contain'
            ? Math.min(boxW / srcW, boxH / srcH)
            : Math.max(boxW / srcW, boxH / srcH);
        scale *= zoom;

        var drawW = srcW * scale, drawH = srcH * scale;
        return {
            x: (boxW - drawW) / 2 + num(place.dx, 0),
            y: (boxH - drawH) / 2 + num(place.dy, 0),
            w: drawW, h: drawH,
            /* Effective print resolution of the placed image, in dots per inch.
             * The only honest number for "will this photograph hold up at
             * 90 mm wide", and the one preflight grades. */
            dpi: drawW > 0 ? Math.round(srcW / (drawW / 25.4)) : 0,
            rot: s.rot, flip: s.flip,
        };
    }

    /* Draw the source into the box (x, y, w, h) with the spec's quarter turns
     * and mirror. `w × h` is the box the TURNED picture occupies, so for a
     * quarter turn the file itself is drawn h × w and rotated into place.
     * The mirror is applied outermost — after the turn — so "отразить" mirrors
     * what is on screen, whichever way the picture has been turned. */
    function paint(g, img, x, y, w, h, s) {
        if (!s.rot && !s.flip) { g.drawImage(img, x, y, w, h); return; }
        var quarter = (s.rot === 90 || s.rot === 270);
        var iw = quarter ? h : w, ih = quarter ? w : h;
        g.save();
        g.translate(x + w / 2, y + h / 2);
        if (s.flip) g.scale(-1, 1);
        if (s.rot) g.rotate(s.rot * Math.PI / 180);
        g.drawImage(img, -iw / 2, -ih / 2, iw, ih);
        g.restore();
    }

    /* ── the bake ───────────────────────────────────────────────────────────
     * `out` is the pixel size of the result. `place` is millimetres. Returns a
     * canvas; the caller decides whether it becomes screen pixels or CMYK
     * samples.
     */
    function bake(img, spec, place, colours, out) {
        var s = normalise(spec);
        var W = Math.max(1, Math.round(out.w));
        var H = Math.max(1, Math.round(out.h));
        var pxPerMm = W / place.w;

        var cv = document.createElement('canvas');
        cv.width = W; cv.height = H;
        var g = cv.getContext('2d');

        /* The card's paper under everything: a 'contain' fit and a photograph
         * with transparency both leave bare card, and bare card is paper, not
         * a hole. */
        g.fillStyle = rgbCss(colours && colours.paper);
        g.fillRect(0, 0, W, H);

        var r = sourceRect(img, place, spec);
        var dx = r.x * pxPerMm, dy = r.y * pxPerMm;
        var dw = r.w * pxPerMm, dh = r.h * pxPerMm;

        var blurPx = s.blur * pxPerMm;
        var filter = [];
        if (blurPx > 0.05) filter.push('blur(' + blurPx.toFixed(2) + 'px)');
        if (s.sat !== 100) filter.push('saturate(' + (s.sat / 100).toFixed(3) + ')');

        if (blurPx > 0.05) {
            /* Oversize-and-crop. `pad` is generous — a Gaussian at radius N is
             * not finished at N — and the source is scaled up to cover the
             * padded rectangle so the blur has real pixels to reach for at the
             * edges instead of transparent black. */
            var pad = Math.ceil(blurPx * 3) + 2;
            var tmp = document.createElement('canvas');
            tmp.width = W + pad * 2; tmp.height = H + pad * 2;
            var tg = tmp.getContext('2d');
            tg.fillStyle = rgbCss(colours && colours.paper);
            tg.fillRect(0, 0, tmp.width, tmp.height);
            if (filter.length && supportsFilter(tg)) tg.filter = filter.join(' ');
            var grow = (dw + 2 * pad) / dw;
            paint(tg, img,
                dx + pad - (dw * grow - dw) / 2,
                dy + pad - (dh * grow - dh) / 2,
                dw * grow, dh * grow, s);
            tg.filter = 'none';
            g.drawImage(tmp, pad, pad, W, H, 0, 0, W, H);
        } else {
            if (filter.length && supportsFilter(g)) g.filter = filter.join(' ');
            paint(g, img, dx, dy, dw, dh, s);
            g.filter = 'none';
        }

        /* Tint toward the brand colour. `color` keeps the photograph's own
         * luminance and takes only the hue, which is what turns a stock photo
         * into something that belongs on THIS card; a plain alpha wash over it
         * would flatten the picture into a coloured rectangle. */
        if (s.tint > 0 && colours && colours.tint) {
            g.save();
            g.globalCompositeOperation = 'color';
            g.globalAlpha = s.tint / 100;
            g.fillStyle = rgbCss(colours.tint);
            g.fillRect(0, 0, W, H);
            g.restore();
        }

        /* The veil. Toward paper when the type is dark, toward ink when it is
         * light. See the file header for why this rather than `brightness()`. */
        if (s.light !== 0) {
            g.save();
            g.globalAlpha = Math.abs(s.light) / 100;
            g.fillStyle = s.light > 0 ? rgbCss(colours && colours.paper) : rgbCss(colours && colours.ink);
            g.fillRect(0, 0, W, H);
            g.restore();
        }

        cv.__dpi = r.dpi;
        return cv;
    }

    /* Safari shipped `ctx.filter` late enough that a card must still draw
     * without it. Detected rather than sniffed, and the fallback is an
     * unfiltered photograph — visibly not what was asked for, but a card.
     * ⚠️ One answer for the whole site: CanvasCssFilter.works — true where the
     * engine applies ctx.filter OR the page-wide shim does (Safari <= 17, and
     * WebKit builds whose `filter` echoes but does nothing). Reading the value
     * back after assigning it cannot detect anything: on Safari <= 17 the
     * assignment creates a plain JS property that reads back the same string
     * (measured 2026-09-27, slideshow report #8048). The prototype check below
     * is only for a page where the module failed to load. */
    var _filterOk = null;
    function supportsFilter(ctx) {
        if (_filterOk === null) {
            try {
                _filterOk = window.CanvasCssFilter ? window.CanvasCssFilter.works
                    : (typeof CanvasRenderingContext2D !== 'undefined' && 'filter' in CanvasRenderingContext2D.prototype);
            } catch (e) { _filterOk = false; }
        }
        return _filterOk;
    }

    function rgbCss(rgb) {
        if (!rgb || rgb.length < 3) return '#ffffff';
        return 'rgb(' + (rgb[0] | 0) + ',' + (rgb[1] | 0) + ',' + (rgb[2] | 0) + ')';
    }

    /* ── how dark the finished background is, in one number ─────────────────
     * Preflight needs the luminance UNDER a block of text to decide whether the
     * text can be read. It cannot ask the photograph, because the photograph is
     * not what gets printed — the baked result is. Averaging over the block's
     * own rectangle rather than the whole card is the difference between "this
     * picture is mostly bright" and "this NAME sits on the one dark corner".
     *
     * Sampled on a grid rather than every pixel: 24×24 is 576 reads and
     * indistinguishable from the exact mean for this purpose, while a full read
     * of a 1100 px background on every keystroke is not.
     */
    function luminanceIn(canvas, rect) {
        if (!canvas || !canvas.width) return null;
        var x0 = clamp(Math.floor(rect.x), 0, canvas.width - 1);
        var y0 = clamp(Math.floor(rect.y), 0, canvas.height - 1);
        var w = clamp(Math.ceil(rect.w), 1, canvas.width - x0);
        var h = clamp(Math.ceil(rect.h), 1, canvas.height - y0);
        var g = canvas.getContext('2d', { willReadFrequently: true });
        var d;
        try { d = g.getImageData(x0, y0, w, h).data; } catch (e) { return null; }
        var N = 24;
        var sum = 0, n = 0, min = 1, max = 0;
        for (var iy = 0; iy < N; iy++) {
            for (var ix = 0; ix < N; ix++) {
                var px = Math.min(w - 1, Math.floor(ix * w / N));
                var py = Math.min(h - 1, Math.floor(iy * h / N));
                var p = (py * w + px) * 4;
                var L = rel(d[p]) * 0.2126 + rel(d[p + 1]) * 0.7152 + rel(d[p + 2]) * 0.0722;
                sum += L; n++;
                if (L < min) min = L;
                if (L > max) max = L;
            }
        }
        return n ? { mean: sum / n, min: min, max: max } : null;
    }
    function rel(v) {
        var c = v / 255;
        return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    }

    return {
        DEFAULTS: DEFAULTS,
        normalise: normalise,
        isPlain: isPlain,
        key: key,
        sourceRect: sourceRect,
        quarterTurn: quarterTurn,
        bake: bake,
        luminanceIn: luminanceIn,
    };
});
